import React from 'react';
import { createPortal } from 'react-dom';

export interface ZoneHoverTooltipProps {
  /** Real viewport coordinates (event.clientX/clientY) — immune to any
   *  ancestor CSS scaling (e.g. a "fit to container" wrapper), unlike
   *  computing position from world coordinates. */
  x: number;
  y: number;
  name: string;
  categoryName?: string;
  areaSqm?: number;
  /** Desks/workspaces contained in this zone, and how many are free vs
   *  busy right now (from live occupancy data — desks with no such data
   *  count toward `total` but not `free`/`busy`). */
  deskStats?: { total: number; free: number; busy: number } | null;
}

/**
 * Hover tooltip shown over a zone in the viewer — name, category, and
 * (when the map is calibrated) area in m². Portaled to document.body and
 * positioned `fixed` near the cursor, same approach as IconLinkPopover, so
 * it never shrinks with the map's own pan/zoom or an ancestor CSS scale.
 * Purely informational — no click handling, unlike the icon link popover.
 */
export const ZoneHoverTooltip: React.FC<ZoneHoverTooltipProps> = ({
  x,
  y,
  name,
  categoryName,
  areaSqm,
  deskStats
}) => {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: x,
        top: y,
        transform: 'translate(14px, 14px)',
        zIndex: 50,
        pointerEvents: 'none',
        maxWidth: 220,
        borderRadius: 10,
        border: 'none',
        overflow: 'hidden',
        background: 'white',
        boxShadow:
          '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        padding: '8px 10px',
        fontFamily: 'Quicksand, sans-serif'
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 12,
          fontWeight: 600,
          color: '#111827',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {name}
      </p>
      {categoryName && (
        <p style={{ margin: '2px 0 0', fontSize: 11, color: '#6b7280' }}>
          {categoryName}
        </p>
      )}
      {areaSqm !== undefined && (
        <p
          style={{
            margin: '2px 0 0',
            fontSize: 11,
            color: '#6b7280',
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {areaSqm.toFixed(1)} m²
        </p>
      )}
      {deskStats && (
        <p
          style={{
            margin: '4px 0 0',
            paddingTop: 4,
            borderTop: '1px solid rgba(0, 0, 0, 0.06)',
            fontSize: 11,
            color: '#374151',
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {deskStats.total} desk{deskStats.total !== 1 ? 's' : ''}
          {' · '}
          <span style={{ color: '#16a34a' }}>{deskStats.free} free</span>
          {', '}
          <span style={{ color: '#dc2626' }}>{deskStats.busy} busy</span>
        </p>
      )}
    </div>,
    document.body
  );
};
